import { useQuery } from "@tanstack/react-query";
import { Order as OrderType } from "/app/src/models";
import { orderService } from "/app/src/services";
import { useParams } from "react-router-dom";
import OrderDetails from "./orderDetails";
import Lines from "./lines";
import { useState } from "react";
import HeroAlert from "/app/src/components/HeroUi/Alert";
import { useTranslation } from "react-i18next";

/**
 * Renders order details and lines for the specified order.
 * Fetches the order using orderId from route parameters.
 *
 * @returns {JSX.Element} The rendered order details and lines components.
 */
/**
 * Component for processing an order.
 * @returns {JSX.Element}
 */
export default function OrderProcessorOrder() {
  const { orderId } = useParams();
  const [message, setMessage] = useState<AlertMessage>("");
  const { t } = useTranslation();

  const { data: order } = useQuery({
    queryKey: ["order", orderId],
    queryFn: () => {
      return orderService.getSingle(orderId);
    },
    initialData: { order: {} },
    select: (data: { order: OrderType }) => {
      return data.order;
    },
  });

  type AlertMessage = "Processing" | "Error" | "Success" | "";

  /**
   * Renders an alert component based on the given message status.
   * @param {AlertMessage} message - The alert message status.
   * @returns {JSX.Element|null} The corresponding alert component or null if no message.
   */
  const renderAlert = (message: AlertMessage) => {
    switch (message) {
      case "Processing":
        return (
          <HeroAlert
            className="mb-[20px]"
            color="primary"
            title={t("translation:order_processing_started")}
          />
        );
      case "Error":
        return (
          <HeroAlert
            className="mb-[20px]"
            color="danger"
            title={t("translation:order_processing_failed")}
          />
        );
      case "Success":
        return (
          <HeroAlert
            className="mb-[20px]"
            color="success"
            title="Order finished processing."
          />
        );
      default:
        return null;
    }
  };
  return (
    <>
      {message && renderAlert(message)}
      <OrderDetails order={order} />
      <Lines setMessage={setMessage} order={order} />
    </>
  );
}
